<template>
  <div class="work-flow-header">
    <div class="block">
      <div
        class="back-arrow-wrapper"
        v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
        title="Zurück"
        @click="goBack"
      >
        <img
          src="../../assets/img/work-flow-icons/arrow-black.svg"
          class="back-arrow"
          alt="back-arrow"
        />
      </div>
      <div class="name-wrapper">
        <input
          placeholder="Enter flow name"
          v-model.trim="process.name"
          :disabled="!isUserHasAccess"
          :class="{
            error: !process.name || process.name.trim() === '',
          }"
        />
      </div>
    </div>
    <div class="block">
      <div
        v-if="isWorkflowActiveUserListVisible"
        class="workflow-active-user-list-container"
      >
        <div
          class="workflow-active-user-list"
          v-if="socketData?.users?.length > 0"
        >
          <b-avatar-group size="38px" overlap="0.25">
            <b-avatar
              v-for="(user, i) in getSocketUsers"
              :key="`${user.id}`"
              class="user-avatar"
              :class="{ 'in-active': !user.active }"
              :id="`popover-target-${user.id}`"
            >
              <div
                @mouseover="updateUserLastActiveTime(user.id, user.lastVisited)"
                class="workflow-active-user-initial-name"
              >
                {{ getSocketUserName(user.id) }}
              </div>
              <b-popover
                :target="`popover-target-${user.id}`"
                triggers="hover"
                placement="top"
              >
                <h6 class="workflow-active-user-name">
                  {{ socketData?.usersData[user.id] }}
                </h6>
                <div class="workflow-active-user-details">
                  <p v-if="user.active" class="user-active">Aktiv</p>
                  <p v-if="!user.active">Zuletzt aktiv</p>
                  <span v-if="!user.active">
                    {{ socketUsersTime[user.id] }}</span
                  >
                </div>
              </b-popover>
            </b-avatar>
          </b-avatar-group>
        </div>
        <div
          id="more-active-users"
          v-if="getMoreSocketUsers.length > 0"
          class="more-active-users"
          @mouseover="isMoreActiveUserVisible = true"
          @mouseleave="isMoreActiveUserVisible = false"
        >
          +{{ getMoreSocketUsers.length }}
        </div>
        <b-popover
          v-if="isMoreActiveUserVisible && getMoreSocketUsers.length > 0"
          :target="`more-active-users`"
          triggers="hover"
          placement="top"
          custom-class="more-active-users-popover"
        >
          <div class="more-active-users-container">
            <h6>Zuletzt aktiv</h6>
            <div>
              <div
                v-for="user in getMoreSocketUsers"
                class="more-active-users-details"
              >
                <p>{{ socketData?.usersData[user.id] }}</p>
                <span v-if="user.active" class="user-active">Aktiv</span>
                <span v-if="!user.active">{{
                  getLastActiveTime(user.lastVisited)
                }}</span>
              </div>
            </div>
          </div>
        </b-popover>
      </div>

      <div class="icon-wrapper">
        <div class="header-btn">
          <button
            class="save-btn"
            :class="{ 'disable-btn': !isUserHasAccess }"
            @click="saveFlow()"
            :disabled="
              activeAction === getWorkFlowActions.save || !isUserHasAccess
            "
          >
            <b-spinner
              label="Spinning"
              small
              style="width: 25px; height: 25px; color: black"
              v-if="activeAction === getWorkFlowActions.save"
            ></b-spinner>
            <template v-else>
              <img src="../../assets/img/work-flow-icons/save.svg" alt="save" />
              Speichern
            </template>
          </button>
          <button
            class="start-btn"
            @click="startFlow()"
            :disabled="
              activeAction === getWorkFlowActions.start ||
              process.tasks.length === 0
            "
          >
            <b-spinner
              label="Spinning"
              small
              style="width: 25px; height: 25px"
              v-if="activeAction === getWorkFlowActions.start"
            ></b-spinner>
            <template v-else>
              <img
                src="../../assets/img/work-flow-icons/start.svg"
                alt="start"
              />
              Start
            </template>
          </button>
        </div>
        <div class="header-icons">
          <router-link :to="getRunWorkflowInfo" target="_blank">
            <div
              class="tools-container"
              v-if="isUserHasAccess && process.type != 'action'"
              v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
              title="Vorschau"
            >
              <img
                src="../../assets/img/work-flow-icons/preview.svg"
                alt="settings"
                class="preview-icon"
              />
            </div>
          </router-link>

          <div
            v-if="getCommonActionPermission"
            class="tools-container"
            v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
            title="Duplizieren"
            @click="duplicateFlow"
          >
            <CustomLoader
              v-if="loadingStates.duplicate"
              overlay="dark"
              absolute
            />
            <img
              v-else
              src="../../assets/img/duplicate.svg"
              class="share-icon"
              alt="duplicate"
            />
          </div>

          <div
            v-if="
              getWorkflowAccessLevel.EDIT_CONTENT !== getFlowAccessLevel &&
              process.type != 'action'
            "
            class="tools-container"
            v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
            title="Einbetten"
            @click="embedFlow"
          >
            <img
              src="../../assets/img/code-tag.svg"
              class="share-icon"
              alt="embed"
            />
          </div>

          <div
            v-if="getFlowAccessLevel != 'owner'"
            class="tools-container"
            v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
            title="Verlassen"
            @click="isLeaveConfirmVisible = true"
          >
            <img
              src="../../assets/img/quit.svg"
              class="leave-icon"
              alt="leave"
            />
          </div>

          <div
            v-if="getCommonActionPermission"
            @click="toggleShareFlowModal()"
            class="tools-container"
            v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
            title="Teilen"
          >
            <img
              src="../../assets/img/work-flow-icons/share.svg"
              class="share-icon"
              alt="share"
            />
          </div>

          <div
            class="tools-container"
            v-if="getCommonActionPermission"
            @click="downloadFlowJson()"
            v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
            title="Herunterladen"
          >
            <img
              src="../../assets/img/work-flow-icons/download.svg"
              alt="download"
              class="download-icon"
            />
          </div>
          <div
            class="tools-container"
            v-if="isUserHasAccess"
            @click="toggleBrandSettingModal()"
            v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
            title="Einstellungen"
          >
            <img
              src="../../assets/img/work-flow-icons/settings.svg"
              alt="settings"
              class="settings-icon"
            />
          </div>
          <div
            class="tools-container"
            v-if="isUserHasAccess"
            @click="toggleLogsModal()"
            v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
            title="Änderungsverlauf"
          >
            <img
              src="../../assets/img/work-flow-icons/log.svg"
              alt="log"
              class="log-icon"
            />
          </div>
          <div
            class="tools-container"
            v-if="getCommonActionPermission"
            @click="onDeleteFLow"
            v-b-tooltip.hover="{
              customClass: 'codelos-tooltip delete-flow-tooltip',
              offset: [10, 20],
            }"
            title="Löschen"
          >
            <img
              src="../../assets/img/work-flow-icons/trash.svg"
              alt="delete"
              class="delete-icon"
            />
          </div>
        </div>
      </div>
    </div>

    <ShareFlowModal
      v-if="isFlowModalVisible"
      class="share-flow-modal"
      @close="toggleShareFlowModal()"
      :flowId="Number(process.id)"
    />
    <RemoveConfirmModal
      v-if="isRemoveConfirmVisible"
      class="remove-confirm-modal"
      @close="toggleRemoveConfirmModal"
      :id="process.id"
      :name="process.name"
      actionType="remove"
      resourceType="flow"
      :isLoadingButton="isLoading"
      @confirmRemove="removeFlow"
    />
    <RemoveConfirmModal
      v-if="isLeaveConfirmVisible"
      class="remove-confirm-modal"
      :id="process.id"
      :name="process.name"
      :isLoadingButton="loadingStates.leave"
      actionType="leave"
      resourceType="flow"
      @close="isLeaveConfirmVisible = false"
      @confirmLeave="leaveFlow"
    />
    <BrandSettingModel
      v-if="isBrandSettingModelOpen"
      class="brand-setting-modal"
      :isBrandSettingModelOpen="isBrandSettingModelOpen"
      :process="process"
      @close="toggleBrandSettingModal"
      @saveBrandSettings="saveBrandSettingsToProcess"
    />
    <WorkFlowLogModal
      v-if="isLogModalVisible"
      :processId="process.id"
      @onClose="isLogModalVisible = false"
    />
    <CopyEmbedModal
      v-if="copyEmbedModal.visible"
      :copyEmbedModal="copyEmbedModal"
      @closeModal="closeCopyEmbedModal"
    />
  </div>
</template>
<script>
import { getFlowLastId } from '@/utils/helpers';
import ShareFlowModal from '../modals/ShareFlowModal.vue';
import RemoveConfirmModal from '../modals/removeConfirmModal.vue';
import BrandSettingModel from '../modals/BrandSettingModel.vue';
import {
  deleteWorkFlowById,
  duplicateWorkflow,
  leaveWorkflow,
} from '@/services/axios/data.api';
import {
  workFlowTypes,
  workFlowAccessLevel,
  workFlowActions,
} from '@/utils/constants/WorkFlowConstants';
import { runFlow } from '@/services/axios/flow-runner.api';
import { mapGetters, mapActions } from 'vuex';
import WorkFlowLogModal from './WorkFlowLogModal.vue';
import {
  pageChartTypes,
  getPagePropChartKey,
} from '@/utils/helpers/PageBuilderHelper.js';

import {
  getIsVueBasedFormBuilder,
  getApiEndpointVersion,
} from '@/utils/helpers.js';
import { getFormElements } from '@/utils/helpers/FormBuilderHelper.js';
import CustomLoader from '../widgets/CustomLoader.vue';
import CopyEmbedModal from '../modals/CopyEmbedModal.vue';

const WORKFLOW_ACTIVE_USERS_VISIBLE_FOR = Object.freeze([
  'marco@codelos.io',
  'louq@codelos.io',
  'stefan@codelos.io',
  'robin@codelos.io',
  'sagar.khatri@codelos.io',
  'janik@codelos.io',
  'michael@codelos.io',
  'angelika.wentland@codelos.io',
  'erika@codelos.io',
  'test_account05@codelos.io',
  'demo@codelos.io',
]);

export default {
  name: 'WorkFlowHeader',
  data() {
    return {
      activeAction: null,
      isLoading: false,
      isFlowModalVisible: false,
      isRemoveConfirmVisible: false,
      isLeaveConfirmVisible: false,
      isBrandSettingModelOpen: false,
      socketUsersTime: {},
      isMoreActiveUserVisible: false,
      isLogModalVisible: false,
      loadingStates: {
        duplicate: false,
        leave: false,
      },
      copyEmbedModal: {
        visible: false,
        loading: false,
        selectedFlow: {},
        elements: [],
      },
    };
  },
  props: {
    process: {
      type: Object,
      required: true,
    },
    isUserHasAccess: {
      type: Boolean,
      required: true,
      default: false,
    },
    isTableIdArray: {
      type: null | Boolean,
      required: true,
      default: false,
    },
    flowRunnerEndpoint: {
      type: String,
      required: true,
    },
    hasUnsavedChanges: {
      type: Boolean,
      required: true,
    },
    socketData: {
      type: Object,
      required: false,
    },
    getFlowAccessLevel: {
      type: String,
      required: true,
    },
    setLoader: {
      type: Function,
      required: true,
      default: () => {},
    },
  },
  methods: {
    ...mapActions({
      changeSingleFlowName: 'changeSingleFlowNameAction',
      removeSingleFlowById: 'removeSingleFlowByIdAction',
    }),
    async getAllFlowList() {
      const findIndexOfFlow = this.processList.findIndex((list) => {
        return list.id === this.process.id;
      });
      if (findIndexOfFlow === -1) {
        await this.$store.dispatch('getFlows');
      }
    },
    async goBack(e) {
      if (e.ctrlKey || e.metaKey) {
        const routeData = this.$router.resolve({
          name: 'home',
          query: {
            section: 'flows',
            database: 'process-list',
          },
        });
        window.open(routeData.href, '_blank');
      } else {
        if (this.hasUnsavedChanges) {
          if (
            window.confirm(
              'Sie haben ungespeicherte Daten, wollen Sie die Seite wirklich verlassen?'
            )
          ) {
            this.$emit('onSocketDisconnect');
            await this.getAllFlowList();
            this.$router.push({
              path: '/',
              query: { section: 'flows', database: 'process-list' },
            });
          }
        } else {
          this.$emit('onSocketDisconnect');
          await this.getAllFlowList();
          this.$router.push({
            path: '/',
            query: { section: 'flows', database: 'process-list' },
          });
        }
      }
    },
    setActiveAction(action) {
      this.activeAction = action;
    },
    checkIsWorkFlowValidated() {
      const getMessage = (bool, message) => ({ isValidated: bool, message });

      if (!this.process.name || this.process.name.trim() === '') {
        return getMessage(false, 'Bitte geben Sie den Namen des Workflows ein');
      }

      return getMessage(true, null);
    },
    async startFlow() {
      this.setActiveAction(this.getWorkFlowActions.start);
      const { isValidated, message } = this.checkIsWorkFlowValidated();
      if (!isValidated) {
        this.showToaster(message, 'Fehler', 'danger');
        this.setActiveAction(null);
      } else {
        try {
          const flow = JSON.parse(JSON.stringify(this.process));
          if (flow.formTrigger && flow.formTrigger.length > 0) {
            flow.formTrigger.forEach((data) => {
              data.value = data.value.id;
            });
          }
          flow.tasks.forEach((part, index, arr) => {
            arr[index].output = [];
            arr[index].input = [];
          });
          let formData = new FormData();
          formData.append('flow', JSON.stringify(flow));
          if (flow.formTrigger && flow.formTrigger.length > 0) {
            flow.formTrigger.forEach((e) => {
              formData.append(e.name, e.value);
            });
          }

          const startTaskIndex = this.process.tasks.findIndex(
            (el) => el.name === 'Start'
          );
          if (startTaskIndex > -1) {
            const props = getFormElements(
              this.process.tasks[startTaskIndex],
              this.isVueBasedFormBuilder
            );

            if (props.length > 0) {
              props.forEach((e) => {
                if (workFlowTypes['jsonTypes'].includes(e.type))
                  formData.append(e.name, JSON.stringify(e.value));
                else if (workFlowTypes['listTypes'].includes(e.type)) {
                  e.value.forEach((el) => formData.append(e.name, el));
                } else formData.append(e.name, e.value);
              });
            }

            if (this.process.tasks[startTaskIndex].output) {
              this.process.tasks[startTaskIndex].output.forEach((e) => {
                if (e.type == 'file') formData.append(e.name, e.value);
              });
            }
          }

          const response = await runFlow(this.flowRunnerEndpoint, formData);
          if (response && response.status === 200) {
            this.showToaster('Ausgeführt !', 'Erfolgreich', 'success');
            this.$emit('setRunFlowError', null);
            const potentialLastTasks = this.process.tasks.filter((el) => {
              return !this.process.tasks.some(
                (ele) => ele.previousTaskId === el.id
              );
            });
            const lastTask = potentialLastTasks[potentialLastTasks.length - 1];

            if (lastTask.name === 'JsonMapper') {
              lastTask.output = [
                {
                  name: 'mappedJson',
                  label: 'mapped json',
                  type: 'json',
                  value: response.data,
                },
              ];
              this.$emit('setHtmlViewer', 'json', response.data);
            } else {
              let lastData = response.data[response.data.length - 1];
              lastTask.output = lastData.output;
              this.$emit('setHtmlViewer', 'html', lastData.feedback);
            }
            this.$emit('setHtmlViewer', 'isHtmlViewerVisible', true);
          }
        } catch (error) {
          let message;
          try {
            message = JSON.parse(error?.data?.message);
            if (message) {
              this.showToaster(message['error'], 'Fehlerhaft !', 'danger');
              this.$emit('setRunFlowError', message['task']);
            }
          } catch (e) {
            message = error?.data?.message;
            if (message) {
              this.showToaster(message, 'Fehlerhaft !', 'danger');
            }
          }
        } finally {
          this.setActiveAction(null);
        }
      }
    },
    updatePageNodeValues(pageChildren) {
      pageChildren.forEach((pageNode) => {
        if (pageChartTypes.includes(pageNode.type)) {
          pageNode.value[getPagePropChartKey(pageNode.type)] = {};
        }
        if (pageNode.type == 'col') {
          pageNode.elements.forEach((element) => {
            this.updatePageNodeValues(element.children);
          });
        }
      });
    },
    getFilterProcessData() {
      const process = JSON.parse(JSON.stringify(this.process));
      process.tasks.forEach((task) => {
        if (task.name == 'PageBuilder' && task?.props?.jsonContent?.children) {
          this.updatePageNodeValues(task.props.jsonContent.children);
        }
      });

      return process;
    },
    async saveFlow() {
      if (!this.isUserHasAccess) return;
      this.setActiveAction(this.getWorkFlowActions.save);
      const { isValidated, message } = this.checkIsWorkFlowValidated();
      if (!isValidated) {
        this.showToaster(message, 'Fehler', 'danger');
        this.setActiveAction(null);
      } else {
        try {
          const res = await this.$store.dispatch('saveFlow', {
            flow: this.getFilterProcessData(),
          });
          if (res && typeof res == 'number') {
            this.changeWorkFlowNameInProcessList();
            this.showToaster('Gespeichert !', 'Erfolgreich', 'success');
            this.$emit('setReferenceProcess');
          } else {
            throw new Error(res);
          }
        } catch (error) {
          this.showToaster('Fehler beim Speichern !', 'Fehlerhaft !', 'danger');
        } finally {
          this.setActiveAction(null);
        }
        this.$emit('saveZoom');
      }
    },
    changeWorkFlowNameInProcessList() {
      const currentProcessIndex = this.processList.findIndex(
        (singleProcess) => singleProcess.id === this.process.id
      );
      if (
        currentProcessIndex !== -1 &&
        this.processList[currentProcessIndex].name !== this.process.name
      )
        this.changeSingleFlowName({
          flowIndex: currentProcessIndex,
          newName: this.process.name,
        });
    },
    toggleShareFlowModal() {
      this.isFlowModalVisible = !this.isFlowModalVisible;
    },
    toggleRemoveConfirmModal(isToggle) {
      if (isToggle) this.isRemoveConfirmVisible = !this.isRemoveConfirmVisible;
    },
    toggleBrandSettingModal() {
      this.isBrandSettingModelOpen = !this.isBrandSettingModelOpen;
    },
    toggleLogsModal() {
      this.isLogModalVisible = !this.isLogModalVisible;
    },
    downloadFlowJson() {
      this.setActiveAction(this.getWorkFlowActions.download);
      try {
        const deepCopy = JSON.parse(JSON.stringify(this.process));
        deepCopy.id = getFlowLastId(this);
        deepCopy.owner = '';

        if (this.isTableIdArray) {
          deepCopy.tableID = [];
        }

        if (!this.isTableIdArray) {
          deepCopy.tableID = '';
        }
        // Remove input & output
        deepCopy.tasks.forEach((part, index, arr) => {
          arr[index].output = [];
          arr[index].input = [];
        });
        const dataStr =
          'data:text/json;charset=utf-8,' +
          encodeURIComponent(JSON.stringify(deepCopy));
        const link = document.createElement('a');
        link.setAttribute('href', dataStr);
        link.setAttribute('download', deepCopy.name + '.json');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        this.showToaster('Fehler beim Herunterladen', 'Fehlerhaft !', 'danger');
      } finally {
        this.setActiveAction(null);
      }
    },
    async removeFlow(id) {
      this.isLoading = true;
      try {
        const deleteWorkflowResponse = await deleteWorkFlowById(id);
        if (deleteWorkflowResponse.status == 200) {
          this.removeSingleFlowById(id);
          this.$router.push({
            name: 'home',
            query: { section: 'flows', database: 'process-list' },
          });
        }
      } catch (error) {
        this.showToaster('etwas ist schief gelaufen', 'Fehler', 'danger');
      } finally {
        this.isLoading = false;
        this.toggleRemoveConfirmModal(true);
      }
    },
    saveBrandSettingsToProcess(updatedStyles) {
      this.setActiveAction(this.getWorkFlowActions.setting);
      this.$emit('setProcess', { ...this.process, style: updatedStyles });
      setTimeout(() => {
        this.saveFlow();
      }, 0);
    },
    showToaster(message, title, variant) {
      this.$emit('showToaster', message, title, variant);
    },
    getSocketUserName(userID) {
      const userName = this.socketData?.usersData[userID];
      if (userName) {
        const arr = userName.trim().split(' ');

        if (arr.length === 1) {
          return arr[0].substring(0, 2);
        }

        if (arr.length > 1) {
          return arr.map((word) => word[0]).join('');
        }
      }

      return 'NA';
    },
    getLastActiveTime(lastVisitedTimestamp) {
      const now = Date.now();
      const lastVisited = parseInt(lastVisitedTimestamp);
      const elapsedMilliseconds = now - lastVisited;

      const millisecondsInMinute = 60000;
      const millisecondsInHour = millisecondsInMinute * 60;
      const millisecondsInDay = millisecondsInHour * 24;
      const millisecondsInMonth = millisecondsInDay * 30;

      if (elapsedMilliseconds < millisecondsInMinute) {
        const seconds = Math.round(elapsedMilliseconds / 1000);
        return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
      } else if (elapsedMilliseconds < millisecondsInHour) {
        const minutes = Math.round(elapsedMilliseconds / millisecondsInMinute);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      } else if (elapsedMilliseconds < millisecondsInDay) {
        const hours = Math.round(elapsedMilliseconds / millisecondsInHour);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      } else if (elapsedMilliseconds < millisecondsInMonth) {
        const days = Math.round(elapsedMilliseconds / millisecondsInDay);
        return `${days} day${days !== 1 ? 's' : ''} ago`;
      } else {
        const months = Math.round(elapsedMilliseconds / millisecondsInMonth);
        return `${months} month${months !== 1 ? 's' : ''} ago`;
      }
    },
    updateUserLastActiveTime(id, time) {
      this.$set(this.socketUsersTime, id, this.getLastActiveTime(time));
    },
    async duplicateFlow() {
      try {
        this.loadingStates.duplicate = true;
        const res = await duplicateWorkflow(this.process.id);
        if (res?.data && res.status == 200) {
          this.showToaster(
            'Workflow wurde erfolgreich dupliziert',
            'Erfolg',
            'success'
          );

          setTimeout(() => {
            this.$store.dispatch('getFlows');
          }, 1000);
        }
      } catch (error) {
        this.showToaster(
          error?.message || error?.data?.message || 'Etwas ist schief gelaufen',
          'Fehler',
          'danger'
        );
      } finally {
        this.loadingStates.duplicate = false;
      }
    },
    async embedFlow() {
      this.copyEmbedModal.selectedFlow = this.process;
      this.copyEmbedModal.visible = true;
      this.copyEmbedModal.loading = true;
      try {
        if (this.process.tasks.length > 0) {
          const inputTask = this.process.tasks.find((el) =>
            ['start', 'form'].includes(el?.name?.trim().toLowerCase())
          );
          if (inputTask) {
            this.copyEmbedModal.selectedFlow.executionUrl =
              this.process.settings.apiEndpoint;
            this.copyEmbedModal.elements =
              inputTask?.props?.formTrigger?.elements || [];
            this.copyEmbedModal.loading = false;
          }
        } else {
          this.showToaster('Kein Schema gefunden', 'Fehler', 'danger');
          throw new Error('no schema provided');
        }
      } catch (error) {
        this.showToaster('Kein Schema gefunden', 'Fehler', 'danger');
        this.closeCopyEmbedModal();
      }
    },
    closeCopyEmbedModal() {
      this.copyEmbedModal.visible = false;
      this.copyEmbedModal.selectedFlow = {};
      this.copyEmbedModal.elements = [];
      this.copyEmbedModal.loading = false;
    },
    async leaveFlow(id) {
      try {
        this.setLoader(true);
        this.loadingStates.leave = true;
        const response = await leaveWorkflow(id);

        if (response.status == 200) {
          this.removeSingleFlowById(id);
          this.$router.push({
            path: '/',
            query: { section: 'flows', database: 'process-list' },
          });
        }
      } catch (error) {
        console.log('error :>> ', error);
        this.showToaster(error?.message || error, 'Fehler', 'danger');
      } finally {
        this.loadingStates.leave = false;
        this.isLeaveConfirmVisible = false;
        this.setLoader(false);
      }
    },
    async onDeleteFLow() {
      if (this.process.tasks.length > 0) {
        this.toggleRemoveConfirmModal(true);
      } else {
        try {
          this.setLoader(true);
          await this.removeFlow(this.process.id);
        } catch (error) {
          console.log('error :>> ', error);
        } finally {
          this.setLoader(false);
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      processList: 'getAllFlows',
      getUserId: 'getUserId',
      getUserEmail: 'getUserEmail',
    }),
    isWorkflowActiveUserListVisible() {
      const currentUserEmail = (this.getUserEmail || '').trim().toLowerCase();

      return WORKFLOW_ACTIVE_USERS_VISIBLE_FOR.includes(currentUserEmail);
    },
    getWorkFlowActions() {
      return workFlowActions;
    },
    getSortedSocketUsers() {
      const data =
        this.socketData?.users?.sort((a, b) => {
          if (a.active && !b.active) {
            return 1;
          }
          if (!a.active && b.active) {
            return -1;
          }
          return parseInt(a.lastVisited) - parseInt(b.lastVisited);
        }) || [];
      const currentUserIndex = data.findIndex(
        (user) => user.id == this.getUserId
      );
      if (currentUserIndex !== -1) {
        const currentUser = data.splice(currentUserIndex, 1)[0];
        data.push(currentUser);
      }
      return data;
    },
    getSocketUsers() {
      return this.getSortedSocketUsers.slice(
        -5,
        this.getSortedSocketUsers.length
      );
    },
    getMoreSocketUsers() {
      if (this.getSortedSocketUsers.length <= 5) {
        return [];
      }
      return this.getSortedSocketUsers.slice(0, -5).reverse();
    },

    getRunWorkflowInfo() {
      if (this.isVueBasedFormBuilder) {
        const executionAPIVersion =
          getApiEndpointVersion(this.process.settings.executionEndPoint) || 6;
        return {
          name: 'form-builder',
          params: {
            version: `v${executionAPIVersion}`,
            processID: this.process.id,
          },
        };
      } else {
        return {
          name: 'process-runner',
          params: {
            id: this.process.id,
          },
        };
      }
    },
    isVueBasedFormBuilder() {
      return getIsVueBasedFormBuilder(this.process);
    },
    getWorkflowAccessLevel() {
      return workFlowAccessLevel;
    },
    getCommonActionPermission() {
      return ![
        this.getWorkflowAccessLevel?.EXECUTE,
        this.getWorkflowAccessLevel?.EDIT_CONTENT,
      ].includes(this.getFlowAccessLevel);
    },
  },
  components: {
    ShareFlowModal,
    RemoveConfirmModal,
    BrandSettingModel,
    WorkFlowLogModal,
    CustomLoader,
    CopyEmbedModal,
  },
  emits: [
    'setRunFlowError',
    'setHtmlViewer',
    'setReferenceProcess',
    'setProcess',
    'showToaster',
  ],
};
</script>
<style scoped>
.work-flow-header {
  height: 88px;
  border-bottom: 1px solid #ececed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 3;
  user-select: none;
  flex-shrink: 0;
}

.work-flow-header > div {
  height: 100%;
}

.work-flow-header .block {
  display: flex;
  justify-content: center;
  align-items: center;
}

.work-flow-header .block > div {
  height: 100%;
  display: flex;
  align-items: center;
}

.work-flow-header .back-arrow-wrapper {
  width: 92px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: 1px solid #ececed;
  cursor: pointer;
}

.work-flow-header .back-arrow-wrapper:hover {
  background: #efefef;
}

.work-flow-header .back-arrow-wrapper .back-arrow {
  width: 14px;
  height: 16px;
}
.work-flow-header .block .name-wrapper {
  margin-left: 18px;
}
.work-flow-header .block .name-wrapper input {
  color: #757675;
  font-size: 20px;
  text-align: left;
  font-weight: 500;
  cursor: text;
  line-height: 36px;
  height: 80%;
  width: 400px;
}

.work-flow-header .block .name-wrapper input:focus {
  outline: none;
}

.work-flow-header .block .name-wrapper input.error {
  border: 1px solid red;
}

.work-flow-header .block .name-wrapper input:disabled {
  background: #fff !important;
}

.work-flow-header .block .icon-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 9px;
  margin-right: 23px;
}

.work-flow-header .block .icon-wrapper .tools-container {
  border-radius: 5px;
  cursor: pointer;
  height: 38px;
  width: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.work-flow-header .block .icon-wrapper .tools-container:hover {
  background: #eeeeef;
}
.work-flow-header .block .icon-wrapper .tools-container img {
  width: 35.54px;
  height: 30.63px;
}

.work-flow-header .block .icon-wrapper button {
  border-radius: 5px;
  font-size: 18px;
  border: 1px solid;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  height: 38px;
  transition: all 300ms ease-in-out;
}

.work-flow-header .block .icon-wrapper button:disabled {
  opacity: 0.6;
  pointer-events: none;
  border-color: unset;
}

.work-flow-header .block .icon-wrapper .save-btn {
  min-width: 146px;
  background: #fff;
  border-color: black;
  gap: 14px;
}
.work-flow-header .block .icon-wrapper .save-btn:hover {
  background: #efefef;
  border-color: #efefef;
}
.work-flow-header .block .icon-wrapper .start-btn {
  width: 105px;
  background: #0070ff;
  border-color: #0070ff;
  color: white;
  gap: 17px;
}

.work-flow-header .block .icon-wrapper .save-btn img {
  width: 15px;
  height: 15px;
}

.work-flow-header .block .icon-wrapper .start-btn img {
  width: 10px;
  height: 8px;
}

.work-flow-header .block .icon-wrapper .start-btn:hover {
  box-shadow: 1px 1px 8px #0070ff;
}

.work-flow-header .block .icon-wrapper .tools-container .share-icon {
  width: 24.7px;
  height: 22.2px;
}

.work-flow-header .block .icon-wrapper .tools-container .leave-icon {
  width: 20px;
}

.work-flow-header .block .icon-wrapper .tools-container .delete-icon {
  width: 18px;
  height: 24px;
}

.work-flow-header .block .icon-wrapper .tools-container .settings-icon {
  width: 21px;
  height: 22px;
}

.work-flow-header .block .icon-wrapper .tools-container .log-icon {
  width: 22px;
  height: 22px;
}

.work-flow-header .block .icon-wrapper .tools-container .download-icon {
  width: 17.23px;
  height: 21.75px;
}

.work-flow-header .block .icon-wrapper .tools-container .preview-icon {
  width: 24.7px;
  height: 22.2px;
}

.work-flow-header .share-flow-modal {
  position: fixed;
  user-select: text;
}

.work-flow-header .remove-confirm-modal {
  position: fixed;
  user-select: text;
}

.work-flow-header .brand-setting-modal {
  position: fixed;
  width: 100%;
  user-select: text;
}
.brand-setting-modal ::v-deep .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.3);
}
.brand-setting-modal ::v-deep .modal {
  position: fixed;
}

.work-flow-header .block .icon-wrapper .header-icons {
  display: flex;
  gap: 9px;
  align-items: center;
}
.work-flow-header .block .icon-wrapper .header-btn {
  display: flex;
  gap: 9px;
  align-items: center;
}
.workflow-active-user-list-container {
  display: flex;
  align-items: center;
  min-width: 220px;
  margin-right: 10px;
}

.work-flow-header .block .workflow-active-user-list {
  margin-right: 5px;
  display: block !important;
  width: 100%;
}

.work-flow-header .block .more-active-users {
  font-size: 14px;
  line-height: 1.2;
  margin-right: 15px;
  cursor: pointer;
}

.more-active-users-container .more-active-users-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2px 0;
}
.more-active-users-container .more-active-users-details p {
  font-size: 14px;
  line-height: 1.2;
  margin: 0;
  text-transform: capitalize;
  text-overflow: ellipsis;
  max-width: 150px;
  overflow: hidden;
  white-space: nowrap;
}
.more-active-users-container .more-active-users-details span {
  font-size: 12px;
  line-height: 1.2;
  color: #909090;
}
.more-active-users-container h6,
.workflow-active-user-name {
  font-size: 14px;
  line-height: 1.2;
  text-transform: capitalize;
}

.workflow-active-user-initial-name {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.workflow-active-user-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 5px;
}

.workflow-active-user-details .user-active,
.more-active-users-container .more-active-users-details .user-active {
  color: #23ac42 !important;
}
.workflow-active-user-details p,
.workflow-active-user-details span {
  margin: 0;
  font-size: 12px;
  line-height: 1.2;
}
.workflow-active-user-details span {
  color: #909090;
}

.b-avatar-group ::v-deep .b-avatar-group-inner {
  /* justify-content: flex-end; */
  flex-direction: row-reverse;
}
.b-avatar-group .b-avatar-group-inner .b-avatar {
  background-color: white !important;
  color: #000;
  font-size: 16px;
  line-height: 1.2px;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0px 0.1px 3px 1px rgba(0, 0, 0, 0.1);
  opacity: 1;
}
.b-avatar-group .b-avatar-group-inner .b-avatar.in-active {
  opacity: 0.3;
}
</style>
